// @ts-nocheck
import React, { useState } from 'react';
import { useRecordContext, useNotify } from 'react-admin';
import {
	Card,
	CardContent,
	Typography,
	TextField,
	Button,
	Box,
	Stack,
	CircularProgress,
	Alert,
	Chip,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	searchSchemaQdrant,
	syncProjectSchema,
	SchemaTableResult,
	SchemaSyncProjectResult,
} from '../../Services/quantiAI';

// #region TableResultCard
interface TableResultCardProps {
	table: SchemaTableResult;
}

const TableResultCard = ({ table }: TableResultCardProps) => {
	return (
		<Accordion>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
						{table.full_name}
					</Typography>
					<Chip
						label={`Score: ${(table.score * 100).toFixed(1)}%`}
						size="small"
						color={table.score > 0.7 ? 'success' : table.score > 0.5 ? 'warning' : 'default'}
					/>
					<Chip label={`${table.columns?.length || 0} colonnes`} size="small" variant="outlined" />
				</Box>
			</AccordionSummary>
			<AccordionDetails>
				<Stack spacing={2}>
					{table.description && (
						<Typography variant="body2" color="text.secondary">
							{table.description}
						</Typography>
					)}
					{table.columns && table.columns.length > 0 && (
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Colonne</TableCell>
										<TableCell>Type</TableCell>
										<TableCell>Description</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{table.columns.map((col) => (
										<TableRow key={col.name}>
											<TableCell>
												<code>{col.name}</code>
											</TableCell>
											<TableCell>
												<Chip label={col.type} size="small" variant="outlined" />
											</TableCell>
											<TableCell>{col.description || '-'}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Stack>
			</AccordionDetails>
		</Accordion>
	);
};
// #endregion

// #region SyncResultCard
interface SyncResultCardProps {
	result: SchemaSyncProjectResult;
}

const SyncResultCard = ({ result }: SyncResultCardProps) => {
	return (
		<Alert severity={result.success ? 'success' : 'warning'} sx={{ mt: 2 }}>
			<Typography variant="subtitle2">
				Sync terminé - {result.total_indexed} tables indexées
			</Typography>
			{result.connectors && result.connectors.length > 0 && (
				<Box sx={{ mt: 1 }}>
					{result.connectors.map((c, i) => (
						<Chip
							key={i}
							label={`${c.sku}: ${c.tables_indexed || 0} tables`}
							size="small"
							color={c.success ? 'success' : 'error'}
							sx={{ mr: 0.5, mb: 0.5 }}
						/>
					))}
				</Box>
			)}
			{result.message && (
				<Typography variant="body2" sx={{ mt: 1 }}>
					{result.message}
				</Typography>
			)}
		</Alert>
	);
};
// #endregion

// #region ProjectQdrant
export const ProjectQdrant = () => {
	const record = useRecordContext();
	const notify = useNotify();

	const [query, setQuery] = useState('');
	const [loading, setLoading] = useState(false);
	const [syncing, setSyncing] = useState(false);
	const [results, setResults] = useState<SchemaTableResult[]>([]);
	const [totalFound, setTotalFound] = useState(0);
	const [searched, setSearched] = useState(false);
	const [syncResult, setSyncResult] = useState<SchemaSyncProjectResult | null>(null);

	if (!record) return null;

	const projectFolderId = record.folderId;

	const handleSearch = async () => {
		if (!query.trim()) {
			notify('Entrez un terme de recherche', { type: 'warning' });
			return;
		}

		setLoading(true);
		setSearched(true);
		setSyncResult(null);

		try {
			const response = await searchSchemaQdrant(projectFolderId, query);
			setResults(response.tables || []);
			setTotalFound(response.total_found || 0);
		} catch (error: any) {
			notify(error.message || 'Erreur lors de la recherche', { type: 'error' });
			setResults([]);
			setTotalFound(0);
		} finally {
			setLoading(false);
		}
	};

	const handleSync = async () => {
		setSyncing(true);
		setSyncResult(null);

		try {
			const result = await syncProjectSchema(projectFolderId);
			setSyncResult(result);
			notify(`Sync terminé: ${result.total_indexed} tables indexées`, { type: 'success' });
		} catch (error: any) {
			notify(error.message || 'Erreur lors du sync', { type: 'error' });
		} finally {
			setSyncing(false);
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleSearch();
		}
	};

	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Recherche Schema Qdrant
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Teste la recherche dans l'index Qdrant pour le projet{' '}
					<strong>{projectFolderId}</strong>
				</Typography>

				{/* Search bar */}
				<Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
					<TextField
						fullWidth
						size="small"
						placeholder="Ex: facebook, campaign, impressions..."
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						onKeyPress={handleKeyPress}
						disabled={loading}
					/>
					<Button
						variant="contained"
						onClick={handleSearch}
						disabled={loading || !query.trim()}
						startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
					>
						Rechercher
					</Button>
					<Button
						variant="outlined"
						onClick={handleSync}
						disabled={syncing}
						startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
					>
						Sync Qdrant
					</Button>
				</Box>

				{/* Sync result */}
				{syncResult && <SyncResultCard result={syncResult} />}

				{/* Results */}
				{searched && !loading && (
					<Box sx={{ mt: 2 }}>
						{results.length === 0 ? (
							<Alert severity="info">
								Aucune table trouvée pour "{query}".
								{totalFound === 0 && (
									<>
										<br />
										<strong>
											L'index Qdrant est peut-être vide. Cliquez sur "Sync Qdrant" pour
											indexer les tables du projet.
										</strong>
									</>
								)}
							</Alert>
						) : (
							<>
								<Typography variant="subtitle2" sx={{ mb: 1 }}>
									{totalFound} table(s) trouvée(s) pour "{query}"
								</Typography>
								<Stack spacing={1}>
									{results.map((table, index) => (
										<TableResultCard key={index} table={table} />
									))}
								</Stack>
							</>
						)}
					</Box>
				)}
			</CardContent>
		</Card>
	);
};
// #endregion

export default ProjectQdrant;
